/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * AGENT INSTALLER STEP RENDERER
 * 
 * Component responsável pela renderização dinâmica dos diferentes tipos de steps
 * do wizard de instalação (markdown, HTML, integração, parâmetros, resultado)
 */

import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import '@/app/styles/iframes.css';
import { Check, Rocket, Plus, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { IntegrationRequirement } from '@/shared/components/ui/integration-requirement';
import { IntegrationMultiRequirement } from '@/shared/components/ui/integration-multi-requirement';
import { DynamicParameterFields } from '@/shared/components/ui/dynamic-parameter-fields';
import { AgentWithIntegrations, InstallerStep, WizardState } from '../../types/AgentInstaller';
import type { IntegrationBase } from '@/features/Integrations/types/integration';
import { translateDeep } from '@/shared/utils/i18nHelper';

interface AgentInstallerStepRendererProps {
  step: InstallerStep;
  agent: AgentWithIntegrations;
  wizardState: WizardState;
  onUpdateSelectedIntegrations: (fieldName: string, integrations: IntegrationBase[]) => void;
  onUpdateParameterValues: (values: Record<string, any>) => void;
  onUpdatePromptValues: (values: Array<{ id: string; title: string; content: string; block?: boolean }>) => void;
}

export const AgentInstallerStepRenderer: React.FC<AgentInstallerStepRendererProps> = ({
  step,
  agent,
  wizardState,
  onUpdateSelectedIntegrations,
  onUpdateParameterValues,
  onUpdatePromptValues
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0]; // 'pt' ou 'en'
  const promptsInitialized = useRef(false);

  // Traduzir o step completo (inclui title, description, content, fields)
  const translatedStep = useMemo(() => {
    return translateDeep(step, currentLang);
  }, [step, currentLang]);

  // Traduzir settings do agent (para parâmetros)
  const translatedSettings = useMemo(() => {
    return agent.settings ? translateDeep(agent.settings, currentLang) : agent.settings;
  }, [agent.settings, currentLang]);

  // Inicializar prompts quando entrar no step de prompts
  useEffect(() => {
    if (step.name === 'prompts' && !promptsInitialized.current) {
      const promptsContent = step.content;
      const isPrompts = promptsContent && typeof promptsContent === 'object' && !('format' in promptsContent) && (promptsContent.en || promptsContent.pt);
      
      if (isPrompts) {
        const basePromptsInLang = promptsContent[currentLang as 'en' | 'pt'] || promptsContent['pt'] || promptsContent['en'] || [];
        
        if (basePromptsInLang.length > 0 && wizardState.promptValues.length === 0) {
          const initialPrompts = basePromptsInLang.map((p: any, index: number) => ({
            id: `prompt-${index}-${Date.now()}`,
            title: p.title,
            content: p.content,
            block: p.block || false
          }));
          onUpdatePromptValues(initialPrompts);
          promptsInitialized.current = true;
        }
      }
    }
  }, [step.name, step.content, currentLang, onUpdatePromptValues, wizardState.promptValues.length]);

  // Type guards para content
  const isMarkdownHtmlContent = (content: any): content is { format: 'markdown' | 'html'; value: string } => {
    return content && typeof content === 'object' && 'format' in content && 'value' in content;
  };

  const renderStepContent = () => {
    switch (step.name) {
      case 'readme':
        return (
          <div className="space-y-4">
            {translatedStep.description && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {translatedStep.description}
                </p>
              </div>
            )}
            
            {translatedStep.content && isMarkdownHtmlContent(translatedStep.content) && (
              translatedStep.content.format === 'html' ? (
                <div 
                    className="html-content prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(translatedStep.content.value || '')
                    }} 
                  />
              ) : (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown className="markdown-content">
                    {translatedStep.content.value
                      ? translatedStep.content.value.replace(/\\n/g, '\n')
                      : ''
                    }
                  </ReactMarkdown>
                </div>
              )
            )}
          </div>
        );

      case 'integration':
        return (
          <div className="space-y-6">
            {translatedStep.description && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {translatedStep.description}
                </p>
              </div>
            )}
            
            {translatedStep.fields && Array.isArray(translatedStep.fields) && translatedStep.fields.map((field, index) => (
              field.type === 'integrationMultiRequirement' ? (
                <IntegrationMultiRequirement
                  key={index}
                  field={field}
                  selectedIntegrations={wizardState.selectedIntegrations[field.name] || []}
                  onSelectionChange={(integrations) => onUpdateSelectedIntegrations(field.name, integrations)}
                />
              ) : (
                <IntegrationRequirement
                  key={index}
                  field={field}
                  selectedIntegrations={wizardState.selectedIntegrations[field.name] || []}
                  onSelectionChange={(integrations) => onUpdateSelectedIntegrations(field.name, integrations)}
                />
              )
            ))}
          </div>
        );

      case 'prompts': {
        const promptsList = wizardState.promptValues;

        const handleAddPromptSection = () => {
          const newTitle = t('installation.promptNewSection');
          const uniqueTitle = `${newTitle} ${promptsList.length + 1}`;
          const newPrompt = {
            id: `prompt-new-${Date.now()}`,
            title: uniqueTitle,
            content: ''
          };
          onUpdatePromptValues([...promptsList, newPrompt]);
        };

        const handleRemovePromptSection = (id: string) => {
          const updatedPrompts = promptsList.filter(p => p.id !== id);
          onUpdatePromptValues(updatedPrompts);
        };

        const handleUpdatePromptTitle = (id: string, newTitle: string) => {
          const updatedPrompts = promptsList.map(p =>
            p.id === id ? { ...p, title: newTitle } : p
          );
          onUpdatePromptValues(updatedPrompts);
        };

        const handleUpdatePromptContent = (id: string, newContent: string) => {
          const updatedPrompts = promptsList.map(p =>
            p.id === id ? { ...p, content: newContent } : p
          );
          onUpdatePromptValues(updatedPrompts);
        };

        return (
          <div className="space-y-6">
            {translatedStep.description && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {translatedStep.description}
                </p>
              </div>
            )}

            {promptsList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>{t('installation.noPromptsConfigured')}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddPromptSection}
                  className="mt-4 gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {t('installation.promptAddFirstSection')}
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {promptsList.map((prompt) => (
                    <div key={prompt.id} className={`border rounded-lg p-4 space-y-3 ${prompt.block ? 'bg-muted/30' : 'bg-card'}`}>
                      <div className="flex items-center justify-between gap-3">
                        <input
                          type="text"
                          value={prompt.title}
                          onChange={(e) => handleUpdatePromptTitle(prompt.id, e.target.value)}
                          disabled={prompt.block}
                          className={`flex-1 font-medium bg-transparent border-b-2 transition-colors text-base px-2 py-1 ${
                            prompt.block 
                              ? 'border-transparent cursor-not-allowed opacity-70' 
                              : 'border-transparent hover:border-muted focus:border-primary focus:outline-none'
                          }`}
                          placeholder={t('installation.promptSectionTitle')}
                        />
                        {promptsList.length > 1 && !prompt.block && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleRemovePromptSection(prompt.id)}
                            title={t('installation.promptRemoveSection')}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <textarea
                        value={prompt.content}
                        onChange={(e) => handleUpdatePromptContent(prompt.id, e.target.value)}
                        disabled={prompt.block}
                        className={`w-full min-h-[120px] p-3 text-sm border rounded-md resize-y bg-background text-foreground ${
                          prompt.block
                            ? 'cursor-not-allowed opacity-70 bg-muted/20'
                            : 'focus:outline-none focus:ring-2 focus:ring-primary'
                        }`}
                        placeholder={t('installation.promptPlaceholder')}
                      />
                      <p className="text-xs text-muted-foreground">
                        {prompt.block ? t('installation.promptBlocked') : t('installation.promptHint')}
                      </p>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddPromptSection}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t('installation.promptAddSection')}
                  </Button>
                </div>
              </>
            )}
          </div>
        );
      }

      case 'parameters': {
        const parameters = translatedSettings?.parameters || { fields: [] };
        
        return (
          <div className="space-y-6">
            {translatedStep.description && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {translatedStep.description}
                </p>
              </div>
            )}
            
            {parameters.fields && parameters.fields.length > 0 ? (
              <DynamicParameterFields
                parameters={parameters as unknown as { fields?: { name: string; type: 'string' | 'number' | 'boolean' | 'select' | 'textarea' | 'password'; label: string; description?: string; required?: boolean }[] }}
                values={wizardState.parameterValues}
                onChange={onUpdateParameterValues}
                selectedIntegrations={wizardState.selectedIntegrations as unknown as Record<string, Record<string, unknown>[]>}
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>{t('installation.noConfigurableParameters')}</p>
              </div>
            )}
          </div>
        );
      }

      case 'result':
        return (
          <div className="space-y-6">
            {wizardState.installationComplete ? (
              <div className="space-y-6">
                <div className="text-center py-6">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-green-900 mb-2">
                    🎉 {t('installation.successTitle')}
                  </h4>
                  <p className="text-sm text-green-700">
                    {t('installation.successMessage', { agent: agent.name })}
                  </p>
                </div>

                {translatedStep.description && (
                  <div className="mb-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      {translatedStep.description}
                    </p>
                  </div>
                )}

                {/* Renderização dinâmica dos campos definidos em fields */}
                {translatedStep.fields && Array.isArray(translatedStep.fields) && (
                  <div className="space-y-4">
                    {translatedStep.fields.map((field, index) => {
                      switch (field.type) {
                        case 'agentTriggers':
                          return (
                            <div key={index} className="space-y-4">
                              {wizardState.installationResult?.trigger && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                  <h5 className="font-medium text-blue-900 mb-2">
                                    🎯 {t('installation.triggerGenerated')}
                                  </h5>
                                  <code className="text-sm bg-blue-100 text-blue-800 px-3 py-2 rounded font-mono block">
                                    {wizardState.installationResult.trigger}
                                  </code>
                                  {field.description && (
                                    <div 
                                      className="text-xs text-blue-700 mt-2 prose prose-xs max-w-none"
                                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description) }}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          );

                        case 'markdown':
                        case 'content':
                          return (
                            <div key={index} className="space-y-4">
                              {field.description && (
                                <div className="prose prose-sm max-w-none dark:prose-invert">
                                  <ReactMarkdown className="markdown-content">
                                    {field.description.replace(/\\n/g, '\n')}
                                  </ReactMarkdown>
                                </div>
                              )}
                              
                              {field.value && (
                                <div className="prose prose-sm max-w-none dark:prose-invert">
                                  <ReactMarkdown className="markdown-content">
                                    {field.value.replace(/\\n/g, '\n')}
                                  </ReactMarkdown>
                                </div>
                              )}
                            </div>
                          );

                        default:
                          // Campo genérico - renderiza descrição se houver
                          return field.description ? (
                            <div key={index} className="prose prose-sm max-w-none dark:prose-invert">
                              <ReactMarkdown className="markdown-content">
                                {field.description.replace(/\\n/g, '\n')}
                              </ReactMarkdown>
                            </div>
                          ) : null;
                      }
                    })}
                  </div>
                )}


              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl flex items-center justify-center mb-6 border border-primary/10">
                    <Rocket className="h-7 w-7 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-lg font-medium text-foreground">
                      {t('installation.readyToInstall')}
                    </h4>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                      {t('installation.readyDescription')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            {translatedStep.description && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {translatedStep.description}
                </p>
              </div>
            )}
            
            {translatedStep.content && isMarkdownHtmlContent(translatedStep.content) && (
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown className="markdown-content">
                  {translatedStep.content.value.replace(/\\n/g, '\n')}
                </ReactMarkdown>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-6">
        <div className="max-w-3xl mx-auto">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};
